import type { NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink } from "@apollo/client";
import { useMemo } from "react";

import { getErrorLink } from "@shared/data/client/links/errorLink/errorLink";
import { getAddEventIdLink } from "@shared/data/client/links/getAddEventIdLink/getAddEventIdLink";
import { getAddUTMLink } from "@shared/data/client/links/getAddUTMLink/getAddUTMLink";
import { fingerprintLink } from "@shared/data/client/links/getFingerprintLink";
import { getFrontendMetaDataLink } from "@shared/data/client/links/getFrontendMetaDataLink/getFrontendMetaDataLink";
import { getPersistedQueriesLink } from "@shared/data/client/links/getPersistedQueriesLink/getPersistedQueriesLink";
import { getQueryLoadingLink } from "@shared/data/client/links/getQueryLoadingLink/getQueryLoadingLink";
import { getShouldFrontendRefreshLink } from "@shared/data/client/links/getShouldFrontendRefreshLink/getShouldFrontendRefreshLink";
import { getUploadLink } from "@shared/data/client/links/uploadLink";
import { initializeEventIds } from "@shared/helpers/eventIdManager";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { isProduction, isDevelopment } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { getCache } from "./cache";

const useClient = (): ApolloClient<NormalizedCacheObject> => {
  const [gqlPersistedQueriesFlipper, iamTrustedDeviceEnabled] = useFlippers(
    "gql_persisted_queries",
    "iam_trusted_device"
  );

  const getClient = () => {
    initializeEventIds();

    const getLink = () => {
      const errorLink = getErrorLink(null, true);
      const uploadLink = getUploadLink();

      let links = [
        errorLink,
        getFrontendMetaDataLink(),
        getAddUTMLink(),
        getAddEventIdLink(),
        getShouldFrontendRefreshLink(),
      ];

      if (gqlPersistedQueriesFlipper) {
        const persistedQueriesLink = getPersistedQueriesLink();
        links.push(persistedQueriesLink);
      }
      if (isDevelopment()) {
        const queryLoadingLink = getQueryLoadingLink();
        links = [...links, queryLoadingLink];
      }
      if (iamTrustedDeviceEnabled) {
        links.push(fingerprintLink);
      }

      return ApolloLink.from([...links, uploadLink]);
    };

    const cache = getCache();

    return new ApolloClient({
      link: getLink(),
      cache,
      connectToDevTools: !isProduction(),
    });
  };

  return useMemo(() => getClient(), [gqlPersistedQueriesFlipper]);
};

export { useClient };
